import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import {
  isInquiryProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';

import { getEcolabelsByKeys, getListingEcolabels } from '../../config/ecolabels';

import { EcolabelList, Heading, Modal } from '../../components';
import UserCard from './UserCard/UserCard';
import InquiryForm from './InquiryForm/InquiryForm';

import css from './ListingPage.module.css';

const CONTACT_USER_LINK = 'inquiryModalContactUserLink';

/**
 * "Meet the seller" — who the shopper would be buying from.
 *
 * Alongside the brand's profile it shows every ecolabel the brand has earned
 * across its range (gathered by ListingPage.duck's fetchAuthorEcolabels), which
 * is what a shopper is really asking when they scroll down here: not just "is
 * this product sound?" but "is this brand?".
 *
 * @component
 * @param {Object} props
 * @param {propTypes.listing} props.listing the listing being viewed
 * @param {Array<string>} [props.authorEcolabelKeys] canonical ecolabel keys gathered
 *   from the seller's listings
 * @param {string} [props.inquiryRecaptchaErrorId] translation key for a failed bot check
 * @param {boolean} [props.inquiryRecaptchaBlocked] whether the bot check has closed the gate
 * @returns {JSX.Element|null} null when the listing has no author
 */
const SectionAuthorMaybe = props => {
  const {
    title,
    listing,
    authorDisplayName,
    onContactUser,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    currentUser,
    onManageDisableScrolling,
    authorEcolabelKeys = [],
    inquiryRecaptchaErrorId,
    inquiryRecaptchaBlocked,
  } = props;

  if (!listing.author) {
    return null;
  }

  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const unitType = listing?.attributes?.publicData?.unitType || '';
  const isInquiryProcess = isInquiryProcessAlias(transactionProcessAlias);
  const isNegotiationProcess = isNegotiationProcessAlias(transactionProcessAlias);
  const showContact = !(isInquiryProcess || (isNegotiationProcess && unitType === OFFER));

  // The product being viewed is one of the seller's listings, so its own labels
  // belong in the row too. Including them here also means the row is never empty
  // for a product that has ecolabels, even if the seller-wide query failed.
  const sellerEcolabels = getEcolabelsByKeys([
    ...authorEcolabelKeys,
    ...getListingEcolabels(listing?.attributes?.publicData).map(ecolabel => ecolabel.key),
  ]);

  return (
    <section id="author" className={css.sectionAuthor}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.meetTheSellerTitle" />
      </Heading>
      <UserCard
        user={listing.author}
        currentUser={currentUser}
        onContactUser={onContactUser}
        showContact={showContact}
        contactLinkId={CONTACT_USER_LINK}
      />
      {sellerEcolabels.length > 0 ? (
        <div className={css.sellerEcolabels}>
          <Heading as="h3" rootClassName={css.sellerEcolabelsTitle}>
            <FormattedMessage
              id="ListingPage.sellerEcolabelsTitle"
              values={{ name: authorDisplayName }}
            />
          </Heading>
          <EcolabelList ecolabels={sellerEcolabels} size="medium" />
        </div>
      ) : null}
      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={CONTACT_USER_LINK}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
          recaptchaErrorId={inquiryRecaptchaErrorId}
          extraSubmitDisabled={inquiryRecaptchaBlocked}
        />
      </Modal>
    </section>
  );
};

export default SectionAuthorMaybe;
